import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Mail, ArrowLeft, Eye, EyeOff, User, CreditCard, Calendar, Phone, Building2, Store, Shield, Newspaper, Ticket, X } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google';
import { usePublicAuth } from '../context/PublicAuthContext';
import { publicAuthApi } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WelcomeModal from '../components/WelcomeModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_event-platform-39/artifacts/no11df8z_Blanco-Icono.png";

const UserAuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/mi-cuenta';
  const loginMessage = searchParams.get('msg');
  const { loginWithGoogleToken, loginWithEmail, isAuthenticated, needsProfileCompletion } = usePublicAuth();
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState('select'); // 'select', 'login', 'register-email', 'register-verify', 'register-complete'
  const [showPassword, setShowPassword] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [registerForm, setRegisterForm] = useState({
    full_name: '',
    dni: '',
    birth_date: '',
    phone: '',
    password: '',
    confirm_password: '',
    email_consent: false
  });

  // Welcome modal state
  const [welcomeModal, setWelcomeModal] = useState({
    isOpen: false,
    userName: '',
    linkedAccounts: [],
    isNewUser: false,
    isFullBravx: false
  });

  // Verificar que Google SDK esté listo
  useEffect(() => {
    const checkGoogleReady = () => {
      if (window.google?.accounts?.id) {
        setGoogleReady(true);
      } else {
        setTimeout(checkGoogleReady, 100);
      }
    };
    checkGoogleReady();
  }, []);

  // Show login required notification when coming from checkout
  useEffect(() => {
    if (loginMessage === 'login_required') {
      // Use a slightly delayed toast to ensure the page is fully rendered
      const timer = setTimeout(() => {
        toast(
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-xl flex items-center justify-center">
              <Ticket size={24} className="text-black" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-white text-base">¡Estás a un paso!</p>
              <p className="text-zinc-400 text-sm mt-0.5">Iniciá sesión o creá una cuenta para completar tu compra</p>
            </div>
          </div>,
          {
            duration: 6000,
            style: {
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              border: '1px solid rgba(212, 175, 55, 0.5)',
              borderRadius: '16px',
              padding: '16px 20px',
              minWidth: '380px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 30px rgba(212, 175, 55, 0.1)',
            }
          }
        );
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loginMessage]);

  useEffect(() => {
    // Don't auto-navigate if welcome modal is open - let modal handle navigation
    if (isAuthenticated && !needsProfileCompletion && !welcomeModal.isOpen) {
      // Only auto-navigate if no welcome message will be shown
      // (handled by showWelcomeMessage)
    }
  }, [isAuthenticated, needsProfileCompletion, welcomeModal.isOpen]);

  // Helper function to show personalized welcome message
  const showWelcomeMessage = (result) => {
    const userName = result.user?.full_name || result.user?.name || result.user?.email?.split('@')[0] || '';
    const linkedAccounts = result.linked_accounts || [];
    const isNewUser = result.is_new_user;
    const isFullBravx = result.user?.subscription_type === 'full_bravx' || result.user?.subscription_unlimited;
    
    console.log('[WelcomeModal] showWelcomeMessage called:', {
      userName,
      linkedAccountsCount: linkedAccounts.length,
      linkedAccounts,
      isNewUser,
      isFullBravx
    });
    
    // Always show the welcome modal (for linked accounts or to show available features)
    console.log('[WelcomeModal] Opening welcome modal');
    setWelcomeModal({
      isOpen: true,
      userName,
      linkedAccounts,
      isNewUser,
      isFullBravx
    });
  };

  const handleWelcomeModalClose = () => {
    setWelcomeModal(prev => ({ ...prev, isOpen: false }));
    navigate(redirectUrl);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const result = await loginWithGoogleToken(credentialResponse.credential);
      console.log('[WelcomeModal] Google login result:', {
        user: result.user?.full_name || result.user?.name,
        linked_accounts: result.linked_accounts,
        is_new_user: result.is_new_user,
        needs_completion: result.needs_completion
      });
      if (result.needs_completion) {
        // Pass redirect URL to profile completion page
        navigate(`/completar-perfil?redirect=${encodeURIComponent(redirectUrl)}`);
      } else {
        showWelcomeMessage(result);
        // Navigation happens after modal closes or immediately if no modal
      }
    } catch (error) {
      console.error('Google auth error:', error);
      // Mostrar mensaje más específico según el error
      let errorMessage = 'Error al iniciar sesión con Google.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Error de conexión. Verifica tu internet e intenta de nuevo.';
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google Login Error:', error);
    // El error común de popup_closed_by_user no es realmente un error
    if (error?.type === 'popup_closed_by_user') {
      return; // No mostrar error si el usuario cerró el popup
    }
    // origin_mismatch significa que la URL no está autorizada en Google Cloud
    if (error?.error === 'origin_mismatch' || error?.message?.includes('origin')) {
      toast.error('Dominio no autorizado. El administrador debe configurar Google Cloud.');
      return;
    }
    toast.error('Error al conectar con Google. Intenta recargar la página.');
  };

  // Email Login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Ingresa email y contraseña');
      return;
    }
    setLoading(true);
    try {
      const result = await loginWithEmail(email, password);
      showWelcomeMessage(result);
      // Navigation happens after modal closes or immediately if no modal
    } catch (error) {
      console.error('Email login error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Send verification code
  const handleInitRegister = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Ingresa tu email');
      return;
    }
    setLoading(true);
    try {
      await publicAuthApi.initRegister(email);
      toast.success('¡Código enviado! Revisa tu email');
      setAuthMode('register-verify');
    } catch (error) {
      console.error('Init register error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar código');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Ingresa el código de 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await publicAuthApi.verifyEmail(email, verificationCode);
      toast.success('¡Email verificado!');
      setAuthMode('register-complete');
    } catch (error) {
      console.error('Verify error:', error);
      toast.error(error.response?.data?.detail || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Complete registration
  const handleCompleteRegister = async (e) => {
    e.preventDefault();
    const { full_name, dni, birth_date, phone, password, confirm_password, email_consent } = registerForm;
    
    if (!full_name || !dni || !birth_date || !password) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }
    if (password !== confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      await publicAuthApi.completeRegister({
        email,
        full_name,
        dni,
        birth_date,
        phone,
        password,
        email_consent
      });
      toast.success('¡Cuenta creada! Iniciando sesión...');
      // Auto login
      await loginWithEmail(email, password);
      navigate(redirectUrl);
    } catch (error) {
      console.error('Complete register error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  const renderBackButton = () => (
    <button
      onClick={() => setAuthMode('select')}
      className="flex items-center text-[#A1A1AA] hover:text-white mb-4 transition-colors"
    >
      <ArrowLeft size={16} className="mr-2" />
      Volver
    </button>
  );

  const renderContent = () => {
    switch (authMode) {
      case 'login':
        return (
          <motion.div
            key="login-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {renderBackButton()}
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">Iniciar Sesión</h2>
            <p className="text-[#A1A1AA] text-sm mb-6">Ingresa con tu email y contraseña</p>
            
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1"
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Contraseña</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-[#1A1A1A] border-[#27272A] mt-1 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-[#D4AF37] font-bold uppercase tracking-wider"
                data-testid="login-submit"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Ingresar
              </Button>
              
              {/* Forgot Password Link */}
              <div className="text-center pt-2">
                <Link 
                  to="/recuperar-password" 
                  className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </form>
          </motion.div>
        );

      case 'register-email':
        return (
          <motion.div
            key="register-email"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {renderBackButton()}
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">Crear Cuenta</h2>
            <p className="text-[#A1A1AA] text-sm mb-6">Paso 1: Verificar tu email</p>
            
            <form onSubmit={handleInitRegister} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1"
                  data-testid="register-email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] font-bold uppercase tracking-wider"
                data-testid="register-send-code"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Mail className="mr-2" size={18} />}
                Enviar Código
              </Button>
            </form>
          </motion.div>
        );

      case 'register-verify':
        return (
          <motion.div
            key="register-verify"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {renderBackButton()}
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">Verificar Email</h2>
            <p className="text-[#A1A1AA] text-sm mb-2">Paso 2: Ingresa el código enviado a</p>
            <p className="text-[#D4AF37] font-mono mb-6">{email}</p>
            
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Código de 6 dígitos</Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1 text-center text-2xl font-mono tracking-[0.5em]"
                  maxLength={6}
                  data-testid="verify-code"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] font-bold uppercase tracking-wider"
                data-testid="verify-submit"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Verificar
              </Button>
              <button
                type="button"
                onClick={handleInitRegister}
                className="w-full text-[#A1A1AA] hover:text-white text-sm"
              >
                Reenviar código
              </button>
            </form>
          </motion.div>
        );

      case 'register-complete':
        return (
          <motion.div
            key="register-complete"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">Completar Registro</h2>
            <p className="text-[#A1A1AA] text-sm mb-6">Paso 3: Tus datos personales</p>
            
            <form onSubmit={handleCompleteRegister} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">
                  <User size={14} className="inline mr-1" /> Nombre Completo *
                </Label>
                <Input
                  type="text"
                  value={registerForm.full_name}
                  onChange={(e) => setRegisterForm({...registerForm, full_name: e.target.value})}
                  placeholder="Juan Pérez"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1"
                  data-testid="register-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">
                    <CreditCard size={14} className="inline mr-1" /> DNI *
                  </Label>
                  <Input
                    type="text"
                    value={registerForm.dni}
                    onChange={(e) => setRegisterForm({...registerForm, dni: e.target.value.replace(/\D/g, '')})}
                    placeholder="12345678"
                    className="bg-[#1A1A1A] border-[#27272A] mt-1"
                    data-testid="register-dni"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">
                    <Calendar size={14} className="inline mr-1" /> Nacimiento *
                  </Label>
                  <Input
                    type="date"
                    value={registerForm.birth_date}
                    onChange={(e) => setRegisterForm({...registerForm, birth_date: e.target.value})}
                    className="bg-[#1A1A1A] border-[#27272A] mt-1"
                    data-testid="register-birth"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">
                  <Phone size={14} className="inline mr-1" /> Teléfono (opcional)
                </Label>
                <Input
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                  placeholder="+54 11 1234-5678"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1"
                  data-testid="register-phone"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Contraseña *</Label>
                <Input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1"
                  data-testid="register-password"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Confirmar Contraseña *</Label>
                <Input
                  type="password"
                  value={registerForm.confirm_password}
                  onChange={(e) => setRegisterForm({...registerForm, confirm_password: e.target.value})}
                  placeholder="Repetir contraseña"
                  className="bg-[#1A1A1A] border-[#27272A] mt-1"
                  data-testid="register-confirm-password"
                />
              </div>
              
              {/* Email Consent Checkbox */}
              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="email_consent"
                  checked={registerForm.email_consent}
                  onChange={(e) => setRegisterForm({...registerForm, email_consent: e.target.checked})}
                  className="mt-1 w-4 h-4 accent-[#D4AF37] bg-[#1A1A1A] border-[#27272A] rounded"
                  data-testid="email-consent-checkbox"
                />
                <label htmlFor="email_consent" className="text-xs text-[#A1A1AA] leading-relaxed">
                  Acepto recibir emails con novedades, promociones y ofertas exclusivas de Bravo Producciones
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] font-bold uppercase tracking-wider"
                data-testid="register-complete-submit"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Crear Cuenta
              </Button>
            </form>
          </motion.div>
        );

      default: // 'select'
        return (
          <motion.div
            key="select"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">Bienvenido</h2>
            <p className="text-[#A1A1AA] text-sm mb-8">Accede a tu cuenta para ver tus tickets</p>

            {/* Google Login */}
            <div className="mb-6">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin text-white" size={24} />
                  <span className="ml-2 text-[#A1A1AA]">Conectando...</span>
                </div>
              ) : !googleReady ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin text-[#52525B]" size={20} />
                  <span className="ml-2 text-[#52525B] text-sm">Cargando Google...</span>
                </div>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  theme="filled_black"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                  width="100%"
                  locale="es"
                  useOneTap={false}
                  auto_select={false}
                />
              )}
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#27272A]"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#121212] px-4 text-[#52525B]">o continuar con email</span>
              </div>
            </div>

            {/* Email options */}
            <div className="space-y-3">
              <Button
                onClick={() => setAuthMode('login')}
                variant="outline"
                className="w-full border-[#27272A] hover:bg-[#1A1A1A] hover:border-white"
                data-testid="email-login-btn"
              >
                <Mail className="mr-2" size={18} />
                Iniciar Sesión con Email
              </Button>
              <Button
                onClick={() => setAuthMode('register-email')}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#B8962E] font-bold"
                data-testid="email-register-btn"
              >
                Crear Cuenta Nueva
              </Button>
            </div>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <section className="pt-36 pb-24 px-6 lg:px-12">
        <div className="max-w-md mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Logo */}
            <motion.div variants={itemVariants} className="text-center mb-8">
              <Link to="/">
                <motion.img 
                  src={LOGO_URL} 
                  alt="Bravo Producciones" 
                  className="h-24 w-auto mx-auto mb-6"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>
            </motion.div>

            {/* Main Card */}
            <motion.div 
              variants={itemVariants}
              className="bg-[#121212] border border-[#27272A] p-8 relative overflow-hidden"
              data-testid="user-auth-card"
            >
              {/* Decorative element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/5 to-transparent pointer-events-none" />
              
              <AnimatePresence mode="wait">
                {renderContent()}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <p className="text-center text-[#52525B] text-xs mb-8">
        Debes ser mayor de 18 años para crear una cuenta
      </p>

      <Footer />

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={welcomeModal.isOpen}
        onClose={handleWelcomeModalClose}
        userName={welcomeModal.userName}
        linkedAccounts={welcomeModal.linkedAccounts}
        isNewUser={welcomeModal.isNewUser}
        isFullBravx={welcomeModal.isFullBravx}
      />
    </div>
  );
};

export default UserAuthPage;
